import { useCallback, useEffect, useMemo, useState } from 'react';

import { Delete02Icon, Exchange01Icon, UploadCircle01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { IDV_OVERALL_STATUS, IDV_OVERALL_STATUS_DISPLAY } from 'constants/idv';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import { uploadSigned } from 'helpers/aws-s3';
import { verifyIdDocument } from 'helpers/document-upload-helpers';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as mime from 'react-native-mime-types';
import { useParams } from 'react-router-dom';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { Button } from 'components/FormComponents/Button/Button';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import styles from './MultipleDocumentUpload.module.scss';

export const MultipleDocumentUpload = ({
  documentTitle = 'Select your files to upload',
  documentDescription = '',
  additionalInstructions = '',
  value = [], // array of ids or models depending on useModel
  modelParams = {},
  className,
  bucketFolder = 'documents',
  maxSize = 20971520,
  caption = '',
  accept,
  brand,
  useModel = false,
  disabled = false,
  idType = '',
  metadata = {},
  listCode = '',
  verifyDocument = false,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  handleStateOnVerify = () => {},
}) => {
  const { t } = useTranslation();
  const { token } = useParams();
  const { verify: verifyByToken } = useVerifyIdDocumentByToken(token || undefined);

  const idUploadTranslations = t(`idUpload`, { ns: 'id_document_types', returnObjects: true });

  const brandingStyleVariables = useBrandingStyleVariables(brand);
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();
  const maxRetries = 3;

  const [uploads, setUploads] = useState([]);

  const [loadingMap, setLoadingMap] = useState({});
  const [errorsMap, setErrorsMap] = useState({});

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        if (mounted) setUploads([]);
        return;
      }

      if (useModel) {
        // ensure metadata shape and default attempts
        const normalized = value.map(v => ({
          ...v,
          metadata: v?.metadata || { ...metadata, id_type: idType, attempts: 0 },
        }));
        if (mounted) setUploads(normalized);
        return;
      }
      try {
        // Since value is an array of objects, treat it as useModel mode
        const normalized = value.map(v => ({
          ...v,
          metadata: v?.metadata || { ...metadata, id_type: idType, attempts: 0 },
        }));
        if (mounted) setUploads(normalized);
      } catch (e) {
        console.error('Error processing uploads:', e);
        if (mounted) setUploads([]);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [value, useModel, metadata, idType]);

  // helpers to update per-upload loading/error
  const setLoadingFor = (key, bool) => setLoadingMap(prev => ({ ...prev, [key]: bool }));
  const setErrorFor = (key, message) => setErrorsMap(prev => ({ ...prev, [key]: message }));

  const callOnChange = useCallback(
    newUploads => {
      // Since we're working with objects, always pass them directly
      onChange(newUploads);
    },
    [onChange],
  );

  const validateRetryCountForUpload = useCallback(
    upload => {
      const attempts = upload?.metadata?.attempts || 0;
      if (attempts >= maxRetries && upload?.status === IDV_OVERALL_STATUS.FAILED) {
        const message =
          "We've accepted your document. Our support team will manually verify and contact you if needed.";
        setErrorFor(upload.id || upload._tempId, message);
        setUploads(prev => prev.map(u => (u.id === upload.id ? { ...u, status: 'accepted' } : u)));
        callOnChange(
          uploads.map(u => (u.id === upload.id ? { ...upload, status: 'accepted' } : u)),
        );
      }
    },
    [uploads, callOnChange],
  );

  // single-file upload flow (used for both new uploads and when multiple files provided)
  const uploadSingleFile = async ({ file, prevUpload = null, tempId = null }) => {
    const key = prevUpload?.id || tempId || file.name;
    setLoadingFor(key, true);
    setErrorFor(key, null);

    try {
      const params = {
        file_name: file.name,
        file_type: file.type,
        file_ext: mime.extension(file.type),
        bucket_folder: bucketFolder,
      };

      const signedFile = await signUpload(params);

      await uploadSigned(signedFile.url, file, file.type);

      const existingAttempts = prevUpload?.metadata?.attempts || 0;
      const attempts = prevUpload ? existingAttempts + 1 : 1;
      const uploadMetadata = {
        ...metadata,
        id_type: idType,
        attempts,
        // Store listCode in metadata for non-ID documents (when verifyDocument is false)
        // This allows requirements calculation to work without creating id_upload_detail records
        ...(listCode && !verifyDocument ? { id_requirements_list: listCode } : {}),
      };

      const uploadData = {
        ...modelParams,
        metadata: uploadMetadata,
        file_name: signedFile.file_name,
        file_type: signedFile.file_type,
        hash_key: signedFile.key,
      };

      const created = await createUpload(uploadData);

      if (prevUpload?.id && prevUpload.id !== created.id) {
        try {
          await deleteUpload(prevUpload.id);
        } catch (err) {
          console.error('Failed to delete previous upload:', err);
        }
      }

      setUploads(prev => {
        if (prevUpload) {
          return prev.map(u => (u.id === prevUpload.id ? created : u));
        }
        return [...prev, created];
      });

      const latest = prev => {
        if (prevUpload) {
          return prev.map(u => (u.id === prevUpload.id ? created : u));
        }
        return [...prev, created];
      };

      // For documents that need verification, create id_upload_detail record
      // For non-ID documents (verifyDocument=false), we store listCode in metadata instead
      if (verifyDocument && listCode && token && created?.id) {
        try {
          await verifyIdDocument(
            {
              uploadId: created.id,
              metadata: uploadMetadata,
              listCode,
              expiryTolerance: null,
              verifyByToken,
              reTakeSelfie: false,
              getUpload,
              useModel: true,
              value: created,
              skipVerification,
            },
            {
              setVerifying: () => {},
              setStatus: () => {},
              setError: () => {},
              setErrorType: () => {},
              setVerificationError: () => {},
              setShowSelfieModal: () => {},
              onChange: () => {},
              setIsFileRejected: () => {},
            },
          );
        } catch (verifyErr) {
          // Log error but don't block the upload
          console.error('Error creating id_upload_detail:', verifyErr);
        }
      }

      setUploads(prevState => {
        const newList = latest(prevState);
        callOnChange(newList);
        validateRetryCountForUpload({ ...created, metadata: uploadMetadata });
        return newList;
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      setErrorFor(
        key,
        idUploadTranslations?.reasonForFailure?.default || 'Upload failed. Please try again.',
      );
    } finally {
      setLoadingFor(key, false);
    }
  };

  const handleFileInput = async event => {
    disableFormActions();
    handleStateOnVerify(true);
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      const tempId = `temp-${Date.now()}-${file.name}`;
      setUploads(prev => [
        ...prev,
        {
          _tempId: tempId,
          file_name: file.name,
          metadata: { ...metadata, id_type: idType, attempts: 0 },
          status: null,
        },
      ]);
      await uploadSingleFile({ file, prevUpload: null, tempId });
    }
    enableFormActions();
    handleStateOnVerify(false);
    event.target.value = '';
  };

  const handleSelectReplaceFile = upload => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = e => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      handleReplace(upload, file);
    };
    input.click();
  };

  const handleReplace = async (existingUpload, newFile) => {
    await uploadSingleFile({ file: newFile, prevUpload: existingUpload });
  };

  const handleFileDelete = async upload => {
    const key = upload.id || upload._tempId || upload.file_name;
    setLoadingFor(key, true);
    setErrorFor(key, null);
    try {
      if (upload.id) {
        await deleteUpload(upload.id);
      }
      const updated = uploads.filter(u =>
        u.id ? u.id !== upload.id : u._tempId !== upload._tempId,
      );
      setUploads(updated);
      callOnChange(updated);
    } catch (err) {
      console.error('[DOCUMENT] deleteUpload failed:', err);
      setErrorFor(key, 'Delete failed. Please try again.');
    } finally {
      setLoadingFor(key, false);
    }
  };

  const renderStatusPill = upload => {
    const status = upload?.status || upload?.verification_status || null; // support variations
    const attempts = upload?.metadata?.attempts || 0;
    if (status === IDV_OVERALL_STATUS.FAILED) {
      if (attempts < maxRetries) {
        return (
          <StatusPill
            text={IDV_OVERALL_STATUS_DISPLAY[status]?.title || 'Failed'}
            status={IDV_OVERALL_STATUS_DISPLAY[status]?.color || 'error'}
          />
        );
      }
      if (status === 'accepted') {
        return <StatusPill text="Support team to verify" status="warning" />;
      }
    }

    if (status && status === IDV_OVERALL_STATUS.VERIFIED) {
      return (
        <StatusPill
          text={IDV_OVERALL_STATUS_DISPLAY[IDV_OVERALL_STATUS.VERIFIED].title}
          status={IDV_OVERALL_STATUS_DISPLAY[IDV_OVERALL_STATUS.VERIFIED].color}
        />
      );
    }

    return (
      <StatusPill
        text={upload.file_name ? 'Uploaded' : 'Not Yet Uploaded'}
        status={upload.file_name ? 'success' : 'neutral'}
      />
    );
  };

  return (
    <>
      <div
        className={cn(styles.root, { [className]: className, 'u-disabled': disabled })}
        style={brandingStyleVariables}>
        <div
          className={cn(
            'u-padding surface-white u-border u-border-radius--medium u-drop-shadow--xs d-flex flex-column u-gap-medium',
          )}>
          <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
            <div className="d-flex flex-column u-gap u-max-upload-card-width">
              {caption && (
                <span className="tertiary-text u-font-size-small u-font-weight-500">{caption}</span>
              )}
              <div className="d-flex flex-column u-gap-x-small">
                <span className="u-font-size-large u-font-weight-500">{documentTitle}</span>
                {documentDescription && (
                  <span className="secondary-text u-font-size-x-small u-font-weight-400">
                    {documentDescription}
                  </span>
                )}
                {additionalInstructions && (
                  <span className="secondary-text u-font-size-x-small u-font-weight-400">
                    {additionalInstructions}
                  </span>
                )}
              </div>
            </div>

            <label
              className={cn(styles.uploadButton, 'u-border', { 'u-disabled': disabled })}
              style={{
                '--brand-button-color': brandingStyleVariables['--button-color'],
                '--brand-button-text-color': brandingStyleVariables['--button-text-color'],
              }}>
              <input
                type="file"
                accept={accept}
                multiple
                onChange={handleFileInput}
                disabled={disabled}
                style={{ display: 'none' }}
              />
              <HugeiconsIcon size="20" icon={UploadCircle01Icon} strokeWidth="1.5" />
              <span className="u-font-size-small u-font-weight-400 primary-text">Upload Files</span>
            </label>
          </div>

          {uploads?.length > 0 && (
            <div className="d-flex flex-column u-gap">
              {uploads.map(item => {
                const key = item.id || item._tempId || item.file_name;
                const error = errorsMap[key];
                return (
                  <div
                    key={key}
                    className="d-flex justify-content-between align-items-center u-border-dashed u-border-radius u-padding surface-tertiary-light">
                    <div className="d-flex flex-column u-gap">
                      <span className="u-font-size-small u-font-weight-500">{item.file_name}</span>
                      <div className="d-flex u-gap align-items-center">
                        {item.metadata?.attempts != null && <>{renderStatusPill(item)}</>}
                        {item.status && (
                          <span className="u-font-size-x-small secondary-text">
                            Status: {item.status}
                          </span>
                        )}
                      </div>
                      {error && (
                        <div className="u-margin-top--xxsmall">
                          <FormMessage
                            message={error}
                            type={MessageType.Error}
                            noMargin
                            closeable={false}
                          />
                        </div>
                      )}
                    </div>

                    <div className="d-flex align-items-center u-gap">
                      <Button
                        type="secondary"
                        edged
                        onClick={() => handleSelectReplaceFile(item)}
                        disabled={disabled || !!loadingMap[key]}>
                        <HugeiconsIcon
                          size="20"
                          icon={Exchange01Icon}
                          strokeWidth="1.5"
                          className="u-margin-right--small"
                        />
                        Replace
                      </Button>

                      <IconComponent
                        icon={Delete02Icon}
                        onClick={() => handleFileDelete(item)}
                        tip="Delete"
                        type="error"
                        size="small"
                        noBackground
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {needsComplementaryDoc && complementaryInstructions && (
            <DisplayBox show={true} alignLeft>
              <span className="u-font-size-small u-font-weight-400">
                {complementaryInstructions}
              </span>
            </DisplayBox>
          )}
        </div>
      </div>
    </>
  );
};

MultipleDocumentUpload.propTypes = {
  documentTitle: PropTypes.string,
  value: PropTypes.array,
  modelParams: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  idType: PropTypes.string.isRequired,
  metadata: PropTypes.object,
  listCode: PropTypes.string,
  verifyDocument: PropTypes.bool,
  skipVerification: PropTypes.bool,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  needsComplementaryDoc: PropTypes.bool,
  complementaryInstructions: PropTypes.string,
};
